import {useEffect, useMemo} from 'react';

import createCache from '@emotion/cache';
import {CacheProvider} from '@emotion/react';
import FuseLayout from '@fuse/core/FuseLayout';
import {selectMainTheme} from '@fuse/core/FuseSettings/fuseSettingsSlice';
import FuseTheme from '@fuse/core/FuseTheme';
import {useAppSelector} from 'app/store/hooks';
import {selectCurrentLanguageDirection} from 'app/store/i18nSlice';
import themeLayouts from 'app/theme-layouts/themeLayouts';
import {useSelector} from 'react-redux';

import GTMPageViewTracker from '@/app/shared-components/GTMPageViewTracker/GTMPageViewTracker';
import {SnackbarProvider} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {initializeWaf} from '@/services';
import InactivityMonitor from '@/services/inactivity/MonitorInactivity';
import {initializeWebView} from '@/services/webview/WebViewFactory';
import logError from '@/utils/errors/logError';

import useDynamicFontLoader from '../hooks/FontsLoader/FontsLoader';

import AuthenticationProvider from './auth/AuthenticationProvider';
import {emotionCacheOptions} from './configs/emotionCacheConfig';
import {initSentry} from './configs/sentryConfig';
import {DevOptionsMenu, ModakLoader} from './shared-components';
import withAppProviders from './withAppProviders';

/**
 * The main App component.
 */
function App() {
  const langDirection = useAppSelector(selectCurrentLanguageDirection);
  const mainTheme = useSelector(selectMainTheme);

  useDynamicFontLoader();
  initSentry();

  useEffect(() => {
    initializeWaf().catch(error => {
      logError(error, {
        fileName: 'App.tsx',
        functionName: 'useEffect initializeWaf',
      });
    });
    initializeWebView();
  }, []);

  // useEffect(() => {
  //   loadFreshChat();
  // }, []);

  const cacheProviderValue = useMemo(
    () => createCache(emotionCacheOptions[langDirection]),
    [langDirection],
  );

  return (
    <CacheProvider value={cacheProviderValue}>
      <FuseTheme
        theme={mainTheme}
        root>
        <InactivityMonitor />
        <DevOptionsMenu />
        <AuthenticationProvider>
          <SnackbarProvider>
            <FuseLayout layouts={themeLayouts} />
            <GTMPageViewTracker />
            <ModakLoader />
          </SnackbarProvider>
        </AuthenticationProvider>
      </FuseTheme>
    </CacheProvider>
  );
}

const AppWithProviders = withAppProviders(App);

export default AppWithProviders;
